import { DestroyRef, Directive, ElementRef, HostListener, inject, input, OnInit, output } from '@angular/core';
import { outputFromObservable } from '@angular/core/rxjs-interop';
import { Subject } from 'rxjs';
import { settingsManager } from 'src/app/game/businesslogic/SettingsManager';
import { PointerInputService } from 'src/app/ui/helper/pointer-input-service';

export const doubleClickTreshhold: number = 250;
export const longPressTreshhold: number = 550;
export const holdTreshhold: number = 500;
export const moveTreshhold: number = 7;
export const repeatInterval: number = 100;
export const dragWidthThreshold: number = 1200;
export const dragWidthFactor: number = 0.4;
export const maxElementDepth: number = 50;
export const wheelEndTreshhold: number = 400;

@Directive({
  selector: 'ghs-pointer-input, [ghs-pointer-input]'
})
export class PointerInputDirective implements OnInit {
  elementRef = inject(ElementRef);
  private service = inject(PointerInputService);

  private destroyRef = inject(DestroyRef);

  readonly clickBehind = input<boolean>(false);
  readonly relative = input<boolean>(false);
  readonly screenWidth = input<boolean>(false);
  readonly repeat = input<boolean>(false);
  readonly disabled = input<boolean>(false);
  readonly draggingDisabled = input<boolean>(false);
  readonly forcePress = input<boolean>(false);
  readonly forceDoubleClick = input<boolean>(false);
  readonly onRelease = input<boolean>(false);
  readonly fastShift = input<boolean>(false);
  readonly scrollWheel = input<boolean>(true);
  private _dragMove$ = new Subject<number>();
  readonly dragMove = outputFromObservable(this._dragMove$);
  private _dragEnd$ = new Subject<number>();
  readonly dragEnd = outputFromObservable(this._dragEnd$);
  readonly dragCancel = output<number>();

  readonly singleClick = output<any>();
  private _doubleClick$ = new Subject<any>();
  readonly doubleClick = outputFromObservable(this._doubleClick$);

  timeout: any = null;
  relativeValue: number = -1;
  value: number = -1;
  repeats: number = -1;
  down: boolean = false;
  clicks: number = 0;
  startX: number = 0;
  startY: number = 0;
  move: boolean = false;
  fast: boolean = false;
  fastOffset: number = 0;
  lastX: number = 0;
  isDragElement: boolean = false;
  wheelBase: number = 0;
  wheelOffset: number = 0;
  wheelTimeout: any = null;

  constructor() {
    this.value = -1;
    this.elementRef.nativeElement.style['touch-action'] = 'pan-y';
    this.isDragElement =
      this.elementRef.nativeElement.hasAttribute('cdkdrag') ||
      this.elementRef.nativeElement.hasAttribute('cdkdraghandle') ||
      this.elementRef.nativeElement.classList.contains('cdk-drag') ||
      this.elementRef.nativeElement.classList.contains('cdk-drag-handle') ||
      this.elementRef.nativeElement.classList.contains('cdk-drag-preview') ||
      this.elementRef.nativeElement.classList.contains('cdk-drag-placeholder');
  }

  ngOnInit(): void {
    this.service.register(this);
    this.destroyRef.onDestroy(() => this.service.unregister(this));
  }

  pointerdown(event: PointerEvent) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    this.down = true;
    this.startX = event.clientX;
    this.startY = event.clientY;
    if (!this.move && this.repeat() && !this._doubleClick$.observed) {
      this.repeats = -1;
      this.repeatTimeout(event);
    } else if (
      this.forcePress() ||
      (settingsManager.settings.pressDoubleClick &&
        !this.forceDoubleClick() &&
        this._doubleClick$.observed &&
        !this.move &&
        event.pointerType === 'touch')
    ) {
      this.timeout = setTimeout(() => {
        if (event.pointerType === 'mouse' || !this.onRelease()) {
          this._doubleClick$.next(event);
        }
        this.timeout = null;
        this.clicks = 2;
      }, longPressTreshhold);
    }
  }

  pointermove(event: PointerEvent) {
    if (this.down) {
      const x = event.clientX;
      const y = event.clientY;
      if (!this.move && Math.max(Math.abs(this.startX - x), Math.abs(this.startY - y)) > moveTreshhold) {
        this.panstart();
      } else if (this.move) {
        this.panmove(event);
      }
    }
  }

  pointerup(event: PointerEvent) {
    if (this.down) {
      this.down = false;
      this.startX = 0;
      this.startY = 0;
      if (this.service.currentPinchZoom) {
        this.clicks = 0;
        if (this.timeout) {
          clearTimeout(this.timeout);
          this.timeout = null;
        }
      } else if (!this.move) {
        if (
          !this.forcePress() &&
          (event.pointerType === 'mouse' || !settingsManager.settings.pressDoubleClick || this.forceDoubleClick())
        ) {
          this.clicks++;
          if (this.timeout) {
            clearTimeout(this.timeout);
            this.timeout = null;
          }
          if (this.clicks === 2 && this._doubleClick$.observed) {
            this._doubleClick$.next(event);
            this.clicks = 0;
          } else {
            this.timeout = setTimeout(
              () => {
                if (this.clicks > 0 && (!this.repeat() || this._doubleClick$.observed)) {
                  this.singleClick.emit(event);
                }
                this.clicks = 0;
                this.timeout = null;
              },
              this._doubleClick$.observed ? doubleClickTreshhold : 0
            );
          }
        } else if (event.pointerType === 'touch' && this.onRelease() && this.clicks === 2) {
          this._doubleClick$.next(event);
          this.clicks = 0;
        } else {
          if (this.clicks < 2 && (!this.repeat() || this._doubleClick$.observed)) {
            this.singleClick.emit(event);
          }
          if (this.timeout) {
            clearTimeout(this.timeout);
            this.timeout = null;
          }
          this.clicks = 0;
        }
      } else {
        this.clicks = 0;
      }
      this.panend();
    }
  }

  panstart() {
    if (
      !this.disabled() &&
      !this.draggingDisabled() &&
      settingsManager.settings.dragValues &&
      (this._dragMove$.observed || this._dragEnd$.observed)
    ) {
      this.elementRef.nativeElement.classList.add('dragging');
    }
    this.move = true;
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
  }

  panmove(event: PointerEvent) {
    if (
      !this.disabled() &&
      !this.draggingDisabled() &&
      settingsManager.settings.dragValues &&
      (this._dragMove$.observed || this._dragEnd$.observed)
    ) {
      const rect = this.elementRef.nativeElement.getBoundingClientRect();
      const x = event.clientX;
      const relative = this.relative();
      if (this.screenWidth()) {
        if (document.body.clientWidth > dragWidthThreshold) {
          this.value = Math.min(
            99,
            Math.max(0, ((x * (dragWidthThreshold / document.body.clientWidth)) / document.body.clientWidth) * 100)
          );
        } else {
          this.value = Math.min(99, Math.max(0, (x / document.body.clientWidth) * 100));
          if (relative) {
            this.value = this.value * dragWidthFactor;
          }
        }
      } else {
        this.value = Math.min(99, Math.max(0, ((x - rect.left) / rect.width) * 100));
      }
      if (relative && this.relativeValue === -1) {
        this.relativeValue = this.value;
      }
      if (this.fast) {
        this.fastOffset += x - this.lastX < 0 ? -1 : 1;
      }
      this.value = Math.floor(relative ? this.value - this.relativeValue : this.value) + this.fastOffset;
      this.lastX = x;
      this._dragMove$.next(this.value);
    }
  }

  panend() {
    if (
      !this.disabled() &&
      !this.draggingDisabled() &&
      settingsManager.settings.dragValues &&
      (this._dragMove$.observed || this._dragEnd$.observed) &&
      (this.value >= 0 || this.relative())
    ) {
      this._dragEnd$.next(this.value);
    }
    this.reset();
  }

  @HostListener('wheel', ['$event'])
  wheel(event: WheelEvent) {
    if (
      this.disabled() ||
      this.draggingDisabled() ||
      !this.scrollWheel() ||
      !settingsManager.settings.dragValues ||
      !(this._dragMove$.observed || this._dragEnd$.observed)
    ) {
      return;
    }

    event.preventDefault();

    if (!this.wheelTimeout) {
      this.elementRef.nativeElement.classList.add('dragging');
      this.wheelOffset = 0;
      if (this.relative()) {
        this.wheelBase = 0;
      } else {
        const rect = this.elementRef.nativeElement.getBoundingClientRect();
        this.wheelBase = Math.floor(Math.min(99, Math.max(0, ((event.clientX - rect.left) / rect.width) * 100)));
      }
    } else {
      clearTimeout(this.wheelTimeout);
    }

    const step = event.shiftKey ? 5 : 1;
    this.wheelOffset += event.deltaY < 0 ? step : -step;
    this.value = this.wheelBase + this.wheelOffset;
    this._dragMove$.next(this.value);

    this.wheelTimeout = setTimeout(() => {
      if (this.value >= 0 || this.relative()) {
        this._dragEnd$.next(this.value);
      }
      this.reset();
      this.wheelTimeout = null;
    }, wheelEndTreshhold);
  }

  cancel() {
    this.down = false;
    this.clicks = 0;
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
    this.dragCancel.emit(this.value);
    this.reset();
  }

  reset() {
    this.repeats = -1;
    this.startX = -1;
    this.startY = -1;
    this.move = false;
    this.value = -1;
    this.relativeValue = -1;
    this.fast = false;
    this.fastOffset = 0;
    this.elementRef.nativeElement.classList.remove('dragging');
  }

  repeatTimeout(event: PointerEvent) {
    if (this.down && !this.move) {
      this.singleClick.emit(event);
      if (this.repeats === -1) {
        this.repeats = holdTreshhold;
      } else {
        this.repeats -= 25;
        if (this.repeats < 25) {
          this.repeats = 25;
        }
      }
      this.timeout = setTimeout(() => {
        this.repeatTimeout(event);
      }, this.repeats);
    }
  }
}
